import React, { useEffect, useState } from "react";
import axios from "axios";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { API } from "@/lib/lifi";

const RANGES = [
  { key: "1", label: "1D" },
  { key: "7", label: "1W" },
  { key: "30", label: "1M" },
];

export default function PriceChart({ symbol }) {
  const [range, setRange] = useState("1");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancel = false;
    if (!symbol) {
      setData([]);
      return;
    }
    setLoading(true);
    axios
      .get(`${API}/chart`, { params: { symbol, days: range } })
      .then((r) => {
        if (cancel) return;
        setData(r.data?.data || []);
      })
      .catch(() => !cancel && setData([]))
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, [symbol, range]);

  const first = data[0]?.y;
  const last = data[data.length - 1]?.y;
  const changePct = first && last ? ((last - first) / first) * 100 : null;
  const up = (changePct ?? 0) >= 0;

  return (
    <div className="panel p-0 overflow-hidden" data-testid="price-chart">
      <div className="flex items-center justify-between px-3 sm:px-4 h-12 border-b border-[var(--border)]">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-[13px] font-semibold text-[var(--text)]">{symbol || "—"}/USD</span>
          <span className="num-sm text-[var(--text)] truncate">
            {last != null
              ? last >= 1
                ? `$${last.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                : `$${last.toFixed(6)}`
              : "—"}
          </span>
          {changePct != null && (
            <span className={`num-sm ${up ? "text-[var(--up)]" : "text-[var(--down)]"}`}>
              {up ? "+" : ""}
              {changePct.toFixed(2)}%
            </span>
          )}
        </div>
        <div className="tabs">
          {RANGES.map((r) => (
            <div
              key={r.key}
              className="tab-item"
              data-active={range === r.key}
              onClick={() => setRange(r.key)}
              data-testid={`chart-range-${r.label}`}
            >
              {r.label}
            </div>
          ))}
        </div>
      </div>
      <div className="h-[220px] sm:h-[260px] p-2">
        {(!data || data.length === 0) ? (
          <div className="h-full flex items-center justify-center text-[12px] text-[var(--text-muted)]">
            {loading ? "Loading chart…" : symbol ? "No chart data" : "Select a token"}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="pc-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor={up ? "#059669" : "#FF6161"} stopOpacity={0.35} />
                  <stop offset="1" stopColor={up ? "#059669" : "#FF6161"} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="t" hide />
              <YAxis domain={["auto", "auto"]} hide />
              <Tooltip
                contentStyle={{
                  background: "#0F1218",
                  border: "1px solid rgba(255,255,255,0.10)",
                  borderRadius: 8,
                  fontSize: 12,
                  color: "#E6E9EF",
                }}
                labelFormatter={(t) => new Date(t).toLocaleString()}
                formatter={(v) => [
                  typeof v === "number"
                    ? v >= 1
                      ? `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                      : `$${v.toFixed(6)}`
                    : v,
                  "Price",
                ]}
              />
              <Area
                type="monotone"
                dataKey="y"
                stroke={up ? "#059669" : "#FF6161"}
                strokeWidth={1.6}
                fill="url(#pc-fill)"
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
